// Apolo IDE — vitest / jest / playwright output parser (Sprint v11 E9).
//
// Accepts the raw stdout of a test command and returns a structured
// summary: pass/fail counts + per-suite + per-test status + first
// failure detail. Tries vitest/jest JSON reporter first; falls back to
// loose text scraping when JSON isn't available.

/**
 * @typedef {{
 *   name: string,
 *   status: 'pass' | 'fail' | 'skip',
 *   duration?: number,
 *   suite?: string,
 *   error?: string,
 * }} TestEntry
 *
 * @typedef {{
 *   ok: boolean,
 *   reporter: 'vitest-json' | 'jest-json' | 'text-fallback',
 *   summary: { total: number, passed: number, failed: number, skipped: number, durationMs?: number },
 *   tests: TestEntry[],
 *   firstFailure?: TestEntry,
 *   raw?: string,
 * }} TestSummary
 */

/**
 * @param {string} output
 * @returns {TestSummary}
 */
export function parseTestOutput(output) {
  if (typeof output !== 'string' || !output) {
    return emptySummary('text-fallback');
  }
  const jsonParsed = tryParseJson(output);
  if (jsonParsed) return jsonParsed;
  return parseText(output);
}

function emptySummary(reporter) {
  return {
    ok: true,
    reporter,
    summary: { total: 0, passed: 0, failed: 0, skipped: 0 },
    tests: [],
  };
}

function tryParseJson(output) {
  // Vitest JSON reporter emits a single JSON object at the end of stdout.
  // Jest --json emits the same shape. We find the last complete JSON object
  // by scanning for the outermost braces.
  const trimmed = output.trim();
  const firstBrace = trimmed.indexOf('{');
  if (firstBrace === -1) return null;
  for (let i = trimmed.length - 1; i > firstBrace; i--) {
    if (trimmed[i] !== '}') continue;
    const candidate = trimmed.slice(firstBrace, i + 1);
    try {
      const json = JSON.parse(candidate);
      // Vitest / Jest shape: { numTotalTests, numPassedTests, ..., testResults: [...] }
      if (typeof json.numTotalTests === 'number') {
        return mapJestLikeJson(json);
      }
    } catch {
      // continue scanning backwards
    }
  }
  return null;
}

function mapJestLikeJson(json) {
  const tests = [];
  for (const suite of json.testResults || []) {
    const suiteName = suite.name || suite.testFilePath || '';
    for (const t of suite.assertionResults || suite.testResults || []) {
      const status = t.status === 'passed' ? 'pass' : t.status === 'failed' ? 'fail' : 'skip';
      tests.push({
        name: t.fullName || t.title || 'test',
        status,
        duration: t.duration ?? undefined,
        suite: suiteName,
        error: status === 'fail'
          ? (Array.isArray(t.failureMessages) ? t.failureMessages.join('\n') : t.failureMessage)
          : undefined,
      });
    }
  }
  const reporter = json.testResults?.some?.((s) => s.endTime) ? 'jest-json' : 'vitest-json';
  return {
    ok: (json.numFailedTests ?? 0) === 0 && (json.success ?? true),
    reporter,
    summary: {
      total: json.numTotalTests || tests.length,
      passed: json.numPassedTests || tests.filter((t) => t.status === 'pass').length,
      failed: json.numFailedTests || tests.filter((t) => t.status === 'fail').length,
      skipped: json.numPendingTests || tests.filter((t) => t.status === 'skip').length,
      durationMs: typeof json.startTime === 'number' && typeof json.endTime === 'number'
        ? json.endTime - json.startTime
        : undefined,
    },
    tests,
    firstFailure: tests.find((t) => t.status === 'fail'),
  };
}

const TEXT_PASS_RE = /✓\s+(.+?)(?:\s+\(([\d.]+)\s*m?s\))?$/gm;
const TEXT_FAIL_RE = /✗\s+(.+?)$|×\s+(.+?)$|FAIL\s+(.+?)$/gm;
const SUMMARY_RE = /(?:Tests?:?\s*)([\d]+)\s+failed[\s,]*(?:[\d]+\s+skipped[\s,]*)?([\d]+)\s+passed/i;
const VITEST_SUMMARY_RE = /Tests\s+(\d+)\s+passed(?:\s+\((\d+)\))?/i;

function parseText(output) {
  const tests = [];
  let m;
  TEXT_PASS_RE.lastIndex = 0;
  while ((m = TEXT_PASS_RE.exec(output)) !== null) {
    tests.push({ name: m[1].trim(), status: 'pass', duration: m[2] ? parseFloat(m[2]) : undefined });
  }
  TEXT_FAIL_RE.lastIndex = 0;
  while ((m = TEXT_FAIL_RE.exec(output)) !== null) {
    const name = (m[1] || m[2] || m[3] || '').trim();
    if (name) tests.push({ name, status: 'fail' });
  }
  const summaryMatch = output.match(SUMMARY_RE) || output.match(VITEST_SUMMARY_RE);
  const passed = tests.filter((t) => t.status === 'pass').length;
  const failed = tests.filter((t) => t.status === 'fail').length;
  return {
    ok: failed === 0 && (!summaryMatch || passed > 0),
    reporter: 'text-fallback',
    summary: {
      total: tests.length,
      passed,
      failed,
      skipped: 0,
    },
    tests,
    firstFailure: tests.find((t) => t.status === 'fail'),
  };
}

/**
 * Build a concise prompt suitable for sending failed-test feedback back
 * to the LLM in the auto-test loop.
 *
 * @param {TestSummary} summary
 * @returns {string}
 */
export function failurePrompt(summary) {
  if (!summary || summary.ok) return '';
  const lines = [];
  lines.push(`Tests failed (${summary.summary.failed} / ${summary.summary.total}).`);
  if (summary.firstFailure) {
    lines.push(`First failure: ${summary.firstFailure.name}`);
    if (summary.firstFailure.error) {
      lines.push('Error:');
      lines.push('```');
      lines.push(summary.firstFailure.error.slice(0, 4_000));
      lines.push('```');
    }
  }
  lines.push('Fix the failing tests by emitting a new <edits> batch.');
  return lines.join('\n');
}
